import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

import { LoaderComponent } from './loader/loader.component';

@Component({
    selector: 'app-roulette-betting-timer',
    standalone: true,
    imports: [CommonModule, LoaderComponent],
    templateUrl: './betting-timer.component.html',
    styleUrl: './betting-timer.component.scss'
})
export class RouletteBettingTimerComponent implements OnChanges, OnDestroy {
    @Input() bettingWindowInSeconds!: number;
    @Input() preparationTimeInSeconds!: number;
    @Input() remainingBettingTimeInSeconds?: number;
    @Input() betsOpenAfterBallThrowInSeconds!: number;
    @Input() ballShootInTimeLeft?: number;
    @Input() isClosed = false;

    progress = 1;
    hurryUp = false;
    ballShootIn = false;

    private readonly smoothTransitionSpeed = 0.5;
    private readonly updateIntervalMs = 50;
    private readonly msPerSecond = 1000;

    private timerEndTime = 0;
    private currentPhaseStartTime = 0;
    private currentPhaseDuration = 0;
    private elapsedAtPhaseStart = 0;
    private intervalId?: ReturnType<typeof setInterval>;
    private targetProgress = 1;
    private isSmoothTransitioning = false;

    get totalArcTime(): number {
        return this.bettingWindowInSeconds;
    }

    get gameBettingTimeInSeconds(): number {
        return this.bettingWindowInSeconds - this.preparationTimeInSeconds - this.betsOpenAfterBallThrowInSeconds;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Priority 1: Handle ball shoot in phase (subPhase 2) - this takes precedence
        // If we have ballShootInTimeLeft, we're in the spinning ball phase
        if ('ballShootInTimeLeft' in changes && this.ballShootInTimeLeft !== undefined && this.ballShootInTimeLeft > 0) {
            this.handleBallShootInPhase();
            return;
        }

        // Priority 2: Handle new round start or page refresh during betting phase
        if ('bettingWindowInSeconds' in changes || 'remainingBettingTimeInSeconds' in changes) {
            this.handleBettingPhaseStart();
        }
    }

    ngOnDestroy(): void {
        this.stopTimer();
    }

    private handleBettingPhaseStart(): void {
        // Use remainingBettingTime for page refresh, otherwise full betting window for new round
        const effectiveBettingTime = this.remainingBettingTimeInSeconds ?? this.bettingWindowInSeconds;

        // If betting time is at or below ball shoot in time, we're waiting for ball
        if (effectiveBettingTime <= this.betsOpenAfterBallThrowInSeconds) {
            this.showBallShootInWaiting();
            return;
        }

        // Calculate elapsed time from start
        const elapsedFromStart = this.bettingWindowInSeconds - effectiveBettingTime;

        this.elapsedAtPhaseStart = elapsedFromStart;
        this.currentPhaseDuration = effectiveBettingTime;
        this.currentPhaseStartTime = Date.now();
        this.timerEndTime = this.currentPhaseStartTime + effectiveBettingTime * this.msPerSecond;

        // Set progress based on elapsed time
        this.progress = 1 - elapsedFromStart / this.totalArcTime;
        this.targetProgress = this.progress;
        this.isSmoothTransitioning = false;

        this.ballShootIn = false;
        this.updateHurryUpState(effectiveBettingTime);

        this.startTimer();
    }

    private showBallShootInWaiting(): void {
        // Betting time expired, waiting for ball to be shot - don't start timer yet
        this.stopTimer();

        this.ballShootIn = true;
        this.hurryUp = false;

        // Position at start of ball shoot in segment (end of preparation phase)
        // Elapsed time = bettingWindow - ballShootInTime (e.g., 30 - 5 = 25s)
        const elapsedAtWaiting = this.bettingWindowInSeconds - this.betsOpenAfterBallThrowInSeconds;
        this.progress = 1 - elapsedAtWaiting / this.totalArcTime;
        this.targetProgress = this.progress;
        this.isSmoothTransitioning = false;
    }

    private handleBallShootInPhase(): void {
        if (this.ballShootInTimeLeft === undefined || this.ballShootInTimeLeft <= 0) {
            return;
        }

        const ballShootInTime = this.ballShootInTimeLeft;

        // We're now in ball shoot in phase
        this.ballShootIn = true;
        this.hurryUp = false;

        // Calculate where we should be in the arc based on timeLeft received
        // Ball shoot in starts after (bettingWindow - ballShootInTime)
        const ballShootInStartTime = this.bettingWindowInSeconds - this.betsOpenAfterBallThrowInSeconds;
        const targetElapsed = ballShootInStartTime + (this.betsOpenAfterBallThrowInSeconds - ballShootInTime);
        const newTargetProgress = 1 - targetElapsed / this.totalArcTime;

        this.targetProgress = newTargetProgress;
        this.isSmoothTransitioning = true;

        // Update phase tracking - elapsed is based on how much time has passed
        this.elapsedAtPhaseStart = targetElapsed;
        this.currentPhaseDuration = ballShootInTime;
        this.currentPhaseStartTime = Date.now();
        this.timerEndTime = this.currentPhaseStartTime + ballShootInTime * this.msPerSecond;

        this.startTimer();
    }

    private startTimer(): void {
        this.stopTimer();

        this.intervalId = setInterval(() => {
            this.updateProgress();
        }, this.updateIntervalMs);
    }

    private stopTimer(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
    }

    private updateProgress(): void {
        const now = Date.now();

        if (now >= this.timerEndTime) {
            this.stopTimer();

            // If we're in betting phase and timer expired, check if we should wait for ball
            if (!this.ballShootIn) {
                const remainingBettingTime = this.currentPhaseDuration - (now - this.currentPhaseStartTime) / this.msPerSecond;
                if (remainingBettingTime <= this.betsOpenAfterBallThrowInSeconds) {
                    this.showBallShootInWaiting();
                }
            }
            return;
        }

        const elapsedInPhase = (now - this.currentPhaseStartTime) / this.msPerSecond;
        const totalElapsed = this.elapsedAtPhaseStart + elapsedInPhase;
        const naturalProgress = 1 - totalElapsed / this.totalArcTime;

        if (this.isSmoothTransitioning) {
            this.targetProgress = naturalProgress;

            const transitionAmount = (this.smoothTransitionSpeed * this.updateIntervalMs) / this.msPerSecond;
            const progressDiff = this.targetProgress - this.progress;
            const progressThreshold = 0.01;
            const transitionMultiplier = 10;

            if (Math.abs(progressDiff) < progressThreshold) {
                this.progress = this.targetProgress;
                this.isSmoothTransitioning = false;
            } else {
                this.progress += progressDiff * transitionAmount * transitionMultiplier;
            }
        } else {
            this.progress = naturalProgress;
            this.targetProgress = naturalProgress;
        }

        // Update hurry up state during betting phase
        if (!this.ballShootIn) {
            const remainingBettingTime = this.currentPhaseDuration - elapsedInPhase;

            // Check if we've reached the waiting phase (betting closed, waiting for ball)
            if (remainingBettingTime <= this.betsOpenAfterBallThrowInSeconds) {
                this.showBallShootInWaiting();
                return;
            }

            this.updateHurryUpState(remainingBettingTime);
        }
    }

    private updateHurryUpState(remainingBettingTime: number): void {
        // Preparation phase = preparationTimeInSeconds + betsOpenAfterBallThrowInSeconds
        const preparationPhaseStart = this.preparationTimeInSeconds + this.betsOpenAfterBallThrowInSeconds;
        this.hurryUp = remainingBettingTime <= preparationPhaseStart && remainingBettingTime > this.betsOpenAfterBallThrowInSeconds;
    }
}
